<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="本所客户档案（Track F）。数据强制本所 firm_id 隔离。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="名称">
        <el-input v-model="query.name" clearable class="!w-160px" />
      </el-form-item>
      <el-form-item label="手机">
        <el-input v-model="query.mobile" clearable class="!w-140px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button @click="resetQuery">重置</el-button>
        <el-button type="success" @click="openForm()">新建客户</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="customerNo" label="客户编号" min-width="150" />
      <el-table-column label="名称" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">{{ displayCustomerName(row.name) }}</template>
      </el-table-column>
      <el-table-column label="类型" width="80">
        <template #default="{ row }">{{ row.customerType === 'ENTERPRISE' ? '企业' : '个人' }}</template>
      </el-table-column>
      <el-table-column prop="mobile" label="手机" width="120" />
      <el-table-column label="分类" width="90">
        <template #default="{ row }">{{ categoryLabel(row.category) }}</template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="100" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openForm(row)">编辑</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="formVisible" :title="form.id ? '编辑客户' : '新建客户'" width="480px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="类型" prop="customerType">
          <el-select v-model="form.customerType" class="!w-100%">
            <el-option label="个人" value="PERSON" />
            <el-option label="企业" value="ENTERPRISE" />
          </el-select>
        </el-form-item>
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" maxlength="128" />
        </el-form-item>
        <el-form-item label="手机">
          <el-input v-model="form.mobile" maxlength="32" />
        </el-form-item>
        <el-form-item label="分类">
          <el-select v-model="form.category" clearable class="!w-100%">
            <el-option label="重点" value="KEY" />
            <el-option label="普通" value="NORMAL" />
            <el-option label="潜在" value="POTENTIAL" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" maxlength="500" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmPracticeApi, type PracticeCustomerVO } from '@/api/firm/practice'
import { dateFormatter } from '@/utils/formatTime'

defineOptions({ name: 'FirmPracticeCustomer' })

const CATEGORY_LABEL: Record<string, string> = {
  KEY: '重点',
  NORMAL: '普通',
  POTENTIAL: '潜在'
}
const PLACEHOLDER_NAMES = new Set(['跳过', '未知', '未填', '无', '测试', '撮合客户'])
const categoryLabel = (v?: string) => (v ? CATEGORY_LABEL[v] || v : '—')
const displayCustomerName = (name?: string) => {
  const n = String(name || '').trim()
  if (!n || PLACEHOLDER_NAMES.has(n)) return '未填写姓名'
  return n
}

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeCustomerVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  name: undefined as string | undefined,
  mobile: undefined as string | undefined
})

const formVisible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  id: undefined as number | undefined,
  customerType: 'PERSON',
  name: '',
  mobile: '',
  category: 'NORMAL',
  remark: ''
})
const rules: FormRules = {
  customerType: [{ required: true, message: '请选择类型', trigger: 'change' }],
  name: [{ required: true, message: '请输入名称', trigger: 'blur' }]
}

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPracticeApi.customerPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const resetQuery = () => {
  query.name = undefined
  query.mobile = undefined
  handleQuery()
}

const openForm = (row?: PracticeCustomerVO) => {
  form.id = row?.id
  form.customerType = row?.customerType || 'PERSON'
  form.name = row?.name || ''
  form.mobile = row?.mobile || ''
  form.category = row?.category || 'NORMAL'
  form.remark = row?.remark || ''
  formVisible.value = true
}

const submit = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    if (form.id) {
      await FirmPracticeApi.customerUpdate({
        id: form.id,
        customerType: form.customerType,
        name: form.name.trim(),
        mobile: form.mobile || undefined,
        category: form.category,
        remark: form.remark || undefined
      })
      message.success('已更新')
    } else {
      await FirmPracticeApi.customerCreate({
        customerType: form.customerType,
        name: form.name.trim(),
        mobile: form.mobile || undefined,
        category: form.category,
        remark: form.remark || undefined
      })
      message.success('已创建')
    }
    formVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

onMounted(getList)
</script>
